import { useState } from 'react';
import { s, authHeaders, Toast, exportToExcel } from './listPageStyles';
import { ListPageShell, ImportWizard } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { ASSET_TYPE_META } from './assetShared';

// Demirbaş Excel İçe Aktarım — tek "Demirbaşlar" sayfası (opsiyonel seri kolonu).
// Satırlar AssetImportRow'a çözülüp POST /api/assets/BulkImport (JSON array) ile gönderilir.
// Yanıt: { inserted, updated, errorCount, errors[] }. Şablon client-side üretilir.
const API_BASE = '/api/assets';

// Türkçe → enum eşlemesi (şablonda hem Türkçe hem enum kabul edilir).
const TYPE_LABEL_TO_ENUM = Object.entries(ASSET_TYPE_META).reduce((acc, [enumVal, label]) => {
  acc[label.toLocaleLowerCase('tr-TR')] = enumVal;
  acc[enumVal.toLocaleLowerCase('tr-TR')] = enumVal;
  return acc;
}, {});

// Excel hücresi (Türkçe ya da İngilizce başlık) → değer.
function cell(row, ...keys) {
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== '') return String(row[k]).trim();
  }
  return '';
}

function normalizeType(raw) {
  if (!raw) return '';
  const key = String(raw).trim().toLocaleLowerCase('tr-TR');
  return TYPE_LABEL_TO_ENUM[key] || String(raw).trim().toUpperCase();
}

// Excel tarih hücresi (Date ya da metin) → ISO.
function toIso(raw) {
  if (!raw) return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw.toISOString();
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function rowToPayload(row) {
  return {
    customerCode: cell(row, 'Müşteri Kodu', 'MusteriKodu', 'customerCode', 'CustomerCode'),
    barcode: cell(row, 'Barkod', 'barcode', 'Barcode'),
    assetType: normalizeType(cell(row, 'Demirbaş Tipi', 'Tip', 'assetType', 'AssetType')),
    modelCode: cell(row, 'Model Kodu', 'modelCode', 'ModelCode'),
    modelName: cell(row, 'Model Adı', 'modelName', 'ModelName'),
    serialNo: cell(row, 'Seri No', 'serialNo', 'SerialNo'),
    status: cell(row, 'Durum', 'status', 'Status') || 'ACTIVE',
    installedAt: toIso(row['Kurulum Tarihi'] ?? row.installedAt ?? row.InstalledAt),
  };
}

const VALID_TYPES = Object.keys(ASSET_TYPE_META);

function validateRow(p) {
  if (!p.customerCode) return 'Müşteri Kodu zorunlu';
  if (!p.assetType) return 'Demirbaş Tipi zorunlu';
  if (!VALID_TYPES.includes(p.assetType)) return `Geçersiz Demirbaş Tipi (${p.assetType})`;
  if (!p.barcode && !p.serialNo) return 'Barkod veya Seri No gerekli';
  return null;
}

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}

export function AssetImportPage() {
  usePageMeta('Demirbaş İçe Aktarım', 'Excel ile toplu demirbaş içe aktarımı.');
  const { canAdd } = usePageActions();
  const [importOpen, setImportOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [toast, setToast] = useState(null);
  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  // Tüm payload'lar tek istekte /api/assets/BulkImport'a gider (PricesPage submitBatch deseni).
  const submitBatch = async (payloads) => {
    const res = await fetch(`${API_BASE}/BulkImport`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify(payloads),
    });
    const body = await res.json().catch(() => null);
    if (!res.ok) {
      const msg = pick(body, 'message', 'Message') || `Sunucu ${res.status}`;
      return { ok: 0, fail: payloads.length, errors: [msg] };
    }
    const result = pick(body, 'data', 'Data') ?? {};
    const inserted = pick(result, 'inserted', 'Inserted') ?? 0;
    const updated = pick(result, 'updated', 'Updated') ?? 0;
    const errorCount = pick(result, 'errorCount', 'ErrorCount') ?? 0;
    const errors = pick(result, 'errors', 'Errors') ?? [];
    setLastResult({ inserted, updated, errorCount });
    return { ok: inserted + updated, fail: errorCount || Math.max(0, payloads.length - inserted - updated), errors };
  };

  const downloadTemplate = () => {
    const sample = [{
      'Müşteri Kodu': 'CUST-0001',
      'Barkod': 'DMB-2026-00001',
      'Demirbaş Tipi': 'Soğutucu',
      'Model Kodu': 'CLR-330',
      'Model Adı': '330L Cam Kapı Soğutucu',
      'Seri No': 'SN-000123',
      'Durum': 'ACTIVE',
      'Kurulum Tarihi': '2026-01-15',
    }];
    exportToExcel(sample, null, 'demirbas_import_sablon');
  };

  const card = {
    background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, rgba(15,23,42,0.10))',
    borderRadius: 12, padding: 20, maxWidth: 820,
  };
  const chip = {
    display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700,
    background: 'var(--accent-soft, rgba(0, 115, 234,0.12))', color: 'var(--accent, #0073ea)',
    fontFamily: 'ui-monospace, monospace', margin: '0 6px 6px 0',
  };

  return (
    <div style={s.container}>
      <ListPageShell
        title="Demirbaş İçe Aktarım"
        subtitle="Excel ile toplu demirbaş içe aktarımı. Şablonu indirin, doldurun ve yükleyin; kayıtlar müşteri kodundan eşleştirilir."
      >
        <div style={card}>
          <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 800, color: 'var(--card-text-strong, #0b2046)' }}>
            Demirbaşlar sayfası
          </h3>
          <p style={{ margin: '0 0 12px', color: 'var(--text-secondary, #6b7280)', lineHeight: 1.6 }}>
            Şablondaki kolon başlıklarını koruyun. Zorunlu alanlar: <strong>Müşteri Kodu</strong>, <strong>Demirbaş Tipi</strong> ve
            (<strong>Barkod</strong> ya da <strong>Seri No</strong>). Demirbaş Tipi Türkçe (Soğutucu, Dolap…) veya enum (COOLER…) yazılabilir.
          </p>
          <div style={{ marginBottom: 16 }}>
            {['Müşteri Kodu', 'Barkod', 'Demirbaş Tipi', 'Model Kodu', 'Model Adı', 'Seri No', 'Durum', 'Kurulum Tarihi'].map((c) => (
              <span key={c} style={chip}>{c}</span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" style={s.btnSecondary} onClick={downloadTemplate}>Şablonu İndir</button>
            {canAdd && <button type="button" style={s.btnPrimary} onClick={() => setImportOpen(true)}>Excel'den İçe Aktar</button>}
          </div>

          {lastResult && (
            <div style={{ marginTop: 18, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ ...chip, background: 'rgba(0,200,117,0.12)', color: '#15803d', fontFamily: 'inherit' }}>Eklenen: {lastResult.inserted}</span>
              <span style={{ ...chip, background: 'rgba(0,115,234,0.12)', color: 'var(--accent)', fontFamily: 'inherit' }}>Güncellenen: {lastResult.updated}</span>
              <span style={{ ...chip, background: 'rgba(226,68,92,0.12)', color: '#b91c1c', fontFamily: 'inherit' }}>Hatalı: {lastResult.errorCount}</span>
            </div>
          )}
        </div>
      </ListPageShell>

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={(okCount) => { if (okCount > 0) showToast('success', `${okCount} demirbaş içe aktarıldı`); }}
        title="Demirbaş İçe Aktar"
        helpText={(
          <>
            Zorunlu alanlar: <strong>Müşteri Kodu</strong>, <strong>Demirbaş Tipi</strong> ve (<strong>Barkod</strong> ya da <strong>Seri No</strong>).
            Var olan barkod/seri güncellenir, yenisi eklenir.
          </>
        )}
        rowToPayload={rowToPayload}
        validateRow={validateRow}
        submitBatch={submitBatch}
        downloadTemplate={downloadTemplate}
      />

      <Toast toast={toast} />
    </div>
  );
}

export default AssetImportPage;
